<template>
  <v-dialog
    v-model="dialogModel"
    max-width="600"
  >
    <v-card
      :title="isEditMode ? 'Editar Opção de Compra' : 'Nova Opção de Compra'"
      :prepend-icon="isEditMode ? 'mdi-pencil' : 'mdi-plus'"
    >
      <v-card-text>
        <v-form ref="form" @submit.prevent="saveOption">
          <v-row dense>
            <v-col cols="12">
              <v-select
                v-model="form.supplierId"
                label="Fornecedor"
                :items="suppliers"
                item-title="description"
                item-value="id"
                required
                :rules="[v => !!v || 'Fornecedor é obrigatório']"
              >
                <template v-slot:prepend-item>
                  <v-list-item @mousedown.stop>
                    <FormFieldDialog
                        :type="GENERAL_OPTIONS.SUPPLIER.id"
                    />
                  </v-list-item>

                  <v-divider class="mt-2"></v-divider>
                </template>
              </v-select>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.price"
                label="Preço Unitário"
                type="number"
                prefix="R$"
                required
                :rules="[v => !!v || 'Preço é obrigatório']"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-text-field
                v-model.number="form.quantity"
                label="Quantidade"
                type="number"
                required
                :rules="[v => !!v || 'Quantidade é obrigatória']"
              ></v-text-field>
            </v-col>
            
            <v-col cols="12" md="4">
              <v-select
                v-model="form.unitId"
                label="Unidade"
                :items="unitTypes"
                item-title="description"
                item-value="id"
                required
                :rules="[v => !!v || 'Unidade é obrigatória']"
              >
                <template v-slot:prepend-item>
                  <v-list-item @mousedown.stop>
                    <FormFieldDialog
                        :type="GENERAL_OPTIONS.UNIT_TYPE.id"
                    />
                  </v-list-item>
                </template>
              </v-select>
            </v-col>
            
            <v-col cols="6">
              <v-select
                v-model="form.paymentMethodId"
                label="Condição de Pagamento"
                :items="paymentMethods" 
                item-title="description"
                item-value="id"
                required
                :rules="[v => !!v || 'Condição de pagamento é obrigatória']"
              >
                <template v-slot:prepend-item>
                  <v-list-item @mousedown.stop>
                    <FormFieldDialog
                        :type="GENERAL_OPTIONS.PAYMENT_METHOD.id"
                    />
                  </v-list-item>
                </template>
              </v-select>
            </v-col>

            <v-col cols="6">
              <v-menu
                v-model="etaMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ props }">
                  <v-text-field
                    v-model="form.eta"
                    label="Previsão de Entrega"
                    readonly
                    :value="formattedEta"
                    v-bind="props"
                  ></v-text-field>
                </template>
              <v-date-picker
                v-model="form.eta"
                title="Previsão de Entrega"
                header="Escolha uma data"
                color="primary"
                @update:model-value="etaMenu = false"
                :min="minDateAllowed"
              ></v-date-picker>
              </v-menu>
            </v-col>
            
            <v-col cols="12">
              <v-textarea
                v-model="form.note"
                label="Observações"
                rows="3"
              ></v-textarea>
            </v-col>
            
            <v-col cols="12">
              <v-list-item>
                <v-list-item-title>Total</v-list-item-title>
                <v-list-item-subtitle class="text-h6 font-weight-bold">
                  {{ formattedTotal }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        
        <v-btn
          text="Cancelar"
          variant="plain"
          @click="close"
        ></v-btn>
        
        <v-btn
          color="primary"
          text="Salvar"
          variant="flat"
          :disabled="!isFormValid"
          @click="saveOption"
        ></v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import FormFieldDialog from '@/components/form-fields/FormFieldDialog.vue'

import { mapGetters } from 'vuex';
import { cleanOptionForServer } from '@/helpers/services/app.services';
import { GENERAL_OPTIONS } from '@/helpers/constants/app.constants';


export default {
  name: 'OptionDialog',

  components: { FormFieldDialog },
  
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },

    option: {
      type: Object,
      default: null
    },

    requestId: {
      type: String,
      default: null
    }
  },
  
  data: () => ({
    GENERAL_OPTIONS,
    etaMenu: false,
    form: {
      id: null,
      requestId: null,
      supplierId: null,
      price: 0,
      quantity: 1,
      unitId: null,
      paymentMethodId: null,
      note: '',
      eta: new Date()
    }
  }),
  
  computed: {
    ...mapGetters('general', ['suppliers', 'paymentMethods', 'unitTypes']),

    formattedEta() {
      const formattedEta = this.form.eta ? this.form.eta.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }) : '';

      return formattedEta;
    },

    dialogModel: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    
    isEditMode() {
      return !!this.option?.id;
    },
    
    isFormValid() {
      return this.form.supplierId && 
             this.form.price > 0 && 
             this.form.quantity > 0 && 
             this.form.unitId && 
             this.form.paymentMethodId &&
             this.form.requestId
    },
    
    formattedTotal() {
      const total = (this.form.price || 0) * (this.form.quantity || 0);
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2
      }).format(total);
    },

    minDateAllowed() {
      return new Date().toISOString().split('T')[0];
    }
  },
  
  watch: {
    option: {
      handler(newOption) {
        if (newOption) {
          this.form = { ...newOption };
          this.form.eta = newOption.eta ? new Date(newOption.eta) : null;
        } else {
          this.resetForm();
        }
      },
      immediate: true
    },
    
    requestId: {
      handler(newRequestId) {
        if (newRequestId) {
          this.form.requestId = newRequestId;
        }
      },
      immediate: true
    }
  },
  
  methods: {
    resetForm() {
      this.form = {
        id: null,
        requestId: this.requestId,
        supplierId: '',
        unitId: null,
        paymentMethodId: null,
        quantity: 1,
        price: 0,
        note: '',
        eta: new Date()
      };
    },
    
    saveOption() {
      if (!this.isFormValid) return;
      
      this.$emit('save', cleanOptionForServer(this.form));
    },
    
    close() {
      this.dialogModel = false;
      this.$emit('close');
    }
  }
}
</script> 